import type { DimensionValue } from 'react-native';
import { XStack, YStack } from 'tamagui';

import PSSkeleton from '@/components/ui/PSSkeleton';
import { space } from '@/constants/tokens';

/**
 * The card bones a carousel or grid shows while its fetch is in flight — home's
 * sections, and the Access Pass landing page's showcase rows.
 *
 * Home's chrome (section headings, the Access Pass block, the tabs) is static
 * copy, not API data, so the screen keeps rendering it and swaps only the CARDS
 * for bones — there's no full-page `HomeSkeleton` the way `app/event/[id]` has
 * one (that page's chrome IS the event, so it can't render until it loads). Each
 * export below drops straight in where a section's cards go:
 *
 *   <Carousel>{data ? cards : <EventCardsSkeleton />}</Carousel>
 *
 * A `<PSSkeleton>` root lives INSIDE each export rather than at the screen — it
 * renders no view (it's a context provider carrying the pulse clock), so the
 * bones still lay out as direct children of the carousel row / grid. The roots
 * all mount in the same commit, so their clocks start on the same frame and the
 * page breathes in unison.
 *
 * ── Why the panel heights are computed, not eyeballed ──────────────────────
 * A carousel row is as tall as its TALLEST card, and a card's height is driven by
 * how far its title wraps. So the bones assume the worst case the real cards
 * allow — a title that runs the full 3 lines `EventCard` caps at (2 for
 * `SecretMenuCard`) — and reserve exactly that height. The row is then already
 * the right size when the real cards arrive, instead of resizing under the
 * user. The maths below is the real card's own layout, re-derived from the same
 * tokens, so it tracks if those change.
 */

// EventCard / SecretMenuCard's DEFAULT_WIDTH. The carousels use it as-is; the
// grid passes its measured column width instead. A raw layout number, same as in
// the cards themselves (width reads Tamagui's unpopulated `size` group).
const CARD_WIDTH = 240;
// The poster's 3:4 portrait ratio, as an `aspectRatio` (width:height) — the bone
// takes its height from its own width, so it needs no measure.
const POSTER_RATIO = 3 / 4;

// Every line of type in both cards is `fontSize="$base"`, whose lineHeight is 20
// in the font config (tamagui.config.ts). One number covers the panel maths.
const LINE_HEIGHT = 20;
// A text-line bone sits a hair under the line height so a stack of them reads as
// separate lines rather than one slab.
const LINE_BONE_H = 16;

/**
 * EventCard's panel at its tallest: a title wrapped to the full 3 lines
 * (`numberOfLines={3}`) plus the dateTime and location lines — 5 lines of type
 * across 3 Text blocks, so 2 `$xs` gaps, inside `$md` padding. 132px.
 */
const EVENT_PANEL_H = space.md * 2 + 5 * LINE_HEIGHT + space.xs * 2;
/**
 * SecretMenuCard's panel at its tallest: a name wrapped to its full 2 lines
 * (`numberOfLines={2}`) plus the one caption line — 3 lines across 2 blocks, so
 * a single `$xs` gap. 88px.
 */
const MENU_PANEL_H = space.md * 2 + 3 * LINE_HEIGHT + space.xs;

/**
 * A carousel card's full height at its tallest — poster, the `$sm` gap, then the
 * 3-line-title panel. Exported because home's ERROR state sizes itself to a card
 * too: the message stands in for a section's worth of content, so it takes the
 * same vertical room the cards would have. One number, derived once.
 */
export const CARD_HEIGHT = CARD_WIDTH / POSTER_RATIO + space.sm + EVENT_PANEL_H;

// The line bones per card, top to bottom. Widths are ragged on purpose so the
// block reads as wrapped text: the last line of each Text block runs short.
const EVENT_LINES: DimensionValue[] = ['100%', '100%', '65%', '80%', '50%'];
const MENU_LINES: DimensionValue[] = ['100%', '70%', '60%'];

/**
 * One card's bones: the poster, then the panel's text lines. Mirrors both cards'
 * anatomy (fixed-ratio poster, `$sm` gap, then a `$md`-padded panel with `$xs`
 * between lines); the two differ only in panel height and line count, so they
 * share this.
 *
 * The panel is a FIXED height (see the header) with no background of its own —
 * bones are `$backgroundSecondary`, which is also the real panel's fill, so
 * drawing the panel would swallow them. Any slack left over by the shorter line
 * bones falls at the panel's foot, exactly where a real card's slack goes.
 */
function CardBones({
  width,
  panelHeight,
  lines,
}: {
  width: number;
  panelHeight: number;
  lines: DimensionValue[];
}) {
  return (
    <YStack width={width}>
      <PSSkeleton.Bone style={{ width: '100%', aspectRatio: POSTER_RATIO }} />
      <YStack height={panelHeight} marginTop="$sm" p="$md" gap="$xs">
        {lines.map((lineWidth, i) => (
          <PSSkeleton.Bone
            key={`${i}-${lineWidth}`}
            style={{ height: LINE_BONE_H, width: lineWidth }}
          />
        ))}
      </YStack>
    </YStack>
  );
}

/** `count` bones' worth of placeholder, as an array (each export maps this). */
const times = (count: number) => Array.from({ length: count }, (_, i) => i);

/**
 * Event-card bones for a home section. `count` is how many to draw — the default
 * 3 overflows a carousel (they scroll, so more would be off-screen work); the
 * grid passes its own count and its measured column width.
 */
export function EventCardsSkeleton({
  count = 3,
  width = CARD_WIDTH,
}: {
  count?: number;
  width?: number;
}) {
  return (
    <PSSkeleton>
      <XStack gap={'$space.md'}>
        {times(count).map((i) => (
          <CardBones
            key={i}
            width={width}
            panelHeight={EVENT_PANEL_H}
            lines={EVENT_LINES}
          />
        ))}
      </XStack>
    </PSSkeleton>
  );
}

/**
 * ShowcaseEventCard's panel at its tallest: a title wrapped to its full 2 lines
 * (`numberOfLines={2}`) and nothing under it, inside `$md` padding. 64px.
 */
const SHOWCASE_PANEL_H = space.md * 2 + 2 * LINE_HEIGHT;

const SHOWCASE_LINES: DimensionValue[] = ['100%', '55%'];

/** Showcase bones for the Access Pass landing page's four carousels. */
export function ShowcaseCardsSkeleton({ count = 3 }: { count?: number }) {
  return (
    <PSSkeleton>
      {times(count).map((i) => (
        <CardBones
          key={i}
          width={CARD_WIDTH}
          panelHeight={SHOWCASE_PANEL_H}
          lines={SHOWCASE_LINES}
        />
      ))}
    </PSSkeleton>
  );
}

/** Secret-menu bones for the Access Pass block's carousel. */
export function SecretMenuCardsSkeleton({ count = 3 }: { count?: number }) {
  return (
    <PSSkeleton>
      {times(count).map((i) => (
        <CardBones
          key={i}
          width={CARD_WIDTH}
          panelHeight={MENU_PANEL_H}
          lines={MENU_LINES}
        />
      ))}
    </PSSkeleton>
  );
}
